import React, { useState } from 'react';

import clsx from 'clsx';
import { Controller, useForm } from 'react-hook-form';
import { formatPhoneNumber } from 'react-phone-number-input';

import { LoadingType, setAccount, setLoadingState } from 'actions/index';
import { WarningImage } from 'assets/icons/index';
import BlurPopup from 'components/BlurPopup/index';
import { Button } from 'components/Buttons/Button/index';
import CodeVerificationForm from 'components/CodeVerificationForm/index';
import FormBody from 'components/FormBody/index';
import Input from 'components/Inputs/Input/index';
import PasswordInput from 'components/Inputs/PasswordInput/index';
import PhoneNumberInput from 'components/Inputs/PhoneNumberInput/index';
import Loader from 'components/Loader/index';
import { AccountSettingsInputName } from 'enums/AccountSettingsInputName';
import { AccountSettingsPopupType } from 'enums/AccountSettingsPopupType';
import { ButtonType } from 'enums/ButtonType';
import { ErrorsMessages } from 'enums/ErrorsMessages';
import { SuccessNotification } from 'enums/SuccessNotification';
import { useAppDispatch } from 'hooks/useAppDispatch';
import useAppNavigate from 'hooks/useAppNavigate';
import { useAppSelector } from 'hooks/useAppSelector';
import * as notificationManager from 'notifications/index';
import { AppRoutes } from 'routes/routes.enums';
import { updateBorrower } from 'thunks/index';
import { removeSpaces } from 'utils/stringUtils';
import { getMessageForRequiredFields, isValidEmail, validatePhoneNumber } from 'utils/validationUtils';
import { FailureNotificationType } from 'notifications';
import styles from 'pages/Dashboard/AccountSettings/AccountSettings.module.scss';
import { applicationApi } from 'api/ApplicationApi';
import { ErrorCodes } from 'enums/ErrorCodes';
import { ErrorDto } from 'errors/ErrorDto';
import { authApi } from 'api/AuthApi';

const AccountSettings = () => {
  const { phone, email } = useAppSelector((state) => state.account);
  const { applicationId } = useAppSelector((state) => state.application);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupType, setPopupType] = useState('');
  const [isNeedToCheckPassword, setIsNeedToCheckPassword] = useState(true);
  const [isNeedToVerificateCode, setIsNeedToVerificateCode] = useState(false);
  const [isPasswordValidationTokenExpired, setIsPasswordValidationTokenExpired] = useState(false);
  const [passwordValidationToken, setPasswordValidationToken] = useState('');
  const [isCodeValid, setIsCodeValid] = useState<boolean | null>(null);
  const {
    [LoadingType.CodeVerification]: isCodeVerifying,
    [LoadingType.AccountSettingsProcessing]: isAccountSettingsProcessing,
    [LoadingType.AccountSettingUpdating]: loading,
  } = useAppSelector((state) => state.loading);
  const dispatch = useAppDispatch();
  const navigator = useAppNavigate();

  const {
    register,
    formState: { errors },
    setValue,
    trigger,
    handleSubmit,
    watch,
    setError,
    control,
    reset,
  } = useForm();
  const watcher = watch();

  const handleOpenPopup = (accountSettingsPopupType: string) => {
    reset();
    setPopupType(accountSettingsPopupType);
    setIsNeedToCheckPassword(true);
    setIsNeedToVerificateCode(false);
    setIsPasswordValidationTokenExpired(false);
    setIsPopupOpen(true);
  };

  const handleOnClose = () => {
    setIsPopupOpen(false);
  };

  const validatePassword = async () => {
    if (
      (AccountSettingsPopupType.Password !== popupType && watcher[AccountSettingsInputName.Password]) ||
      (AccountSettingsPopupType.Password === popupType && watcher[AccountSettingsInputName.OldPassword])
    ) {
      const result = await authApi.verifyPassword(
        watcher[
          AccountSettingsPopupType.Password === popupType
            ? AccountSettingsInputName.OldPassword
            : AccountSettingsInputName.Password
        ],
      );

      if (result.passwordValidationToken) {
        setIsPasswordValidationTokenExpired(false);
        setPasswordValidationToken(result.passwordValidationToken);
        setIsNeedToCheckPassword(false);

        return true;
      } else {
        if (result.statusCode === 403 || result.statusCode === 400) {
          setError(
            AccountSettingsPopupType.Password === popupType
              ? AccountSettingsInputName.OldPassword
              : AccountSettingsInputName.Password,
            { type: 'manual', message: 'Invalid Password. Please check your Password and try again' },
          );
        } else {
          navigator(AppRoutes.Login);
        }

        return false;
      }
    }
  };

  const handleOnResendEmailCode = async () => {
    try {
      setIsCodeValid(null);

      const response = await authApi.sendUpdateEmailVerificationCode({
        email: watcher[AccountSettingsInputName.NewEmail],
        accountPasswordValidationToken: passwordValidationToken,
      });

      if ((response as ErrorDto)?.error?.errorCode === ErrorCodes.EMAIL_IS_ALREADY_USED) {
        setError(AccountSettingsInputName.NewEmail, {
          type: 'manual',
          message: 'This email is already used. Please use different email',
        });

        return;
      }

      setIsNeedToVerificateCode(true);
    } catch (error: any) {
      if (error.message === ErrorsMessages.PasswordTokenExpiredOrInvalid) {
        setIsNeedToCheckPassword(true);
        setIsNeedToVerificateCode(false);
        setIsPasswordValidationTokenExpired(true);
      }

      setError(AccountSettingsInputName.NewEmail, { type: 'manual', message: error.message });
    }
  };

  const handleOnResendPhoneCode = async () => {
    try {
      setIsCodeValid(null);
      await authApi.sendUpdatePhoneNumberSMSCode({
        phone: watcher[AccountSettingsInputName.NewPhone],
        accountPasswordValidationToken: passwordValidationToken,
      });
      setIsNeedToVerificateCode(true);
    } catch (error: any) {
      if (error.message === ErrorsMessages.PasswordTokenExpiredOrInvalid) {
        setIsNeedToCheckPassword(true);
        setIsNeedToVerificateCode(false);
        setIsPasswordValidationTokenExpired(true);
      }

      setError(AccountSettingsInputName.NewPhone, { type: 'manual', message: error.message });
    }
  };

  const onSubmit = async () => {
    dispatch(setLoadingState({ type: LoadingType.AccountSettingsProcessing, loading: true }));

    if (isNeedToCheckPassword) {
      try {
        const passwordValidationResult = await validatePassword();

        if (
          passwordValidationResult &&
          !errors[AccountSettingsInputName.OldPassword]?.message &&
          popupType === AccountSettingsPopupType.Password
        ) {
          await authApi.updatePassword({
            oldPassword: watcher[AccountSettingsInputName.OldPassword],
            newPassword: watcher[AccountSettingsInputName.NewPassword],
          });
          handleOnClose();

          notificationManager.notifySuccess(SuccessNotification.PasswordChangedSuccessfully);
        }
      } catch (error: any) {
        handleOnClose();

        notificationManager.notifyFailure(FailureNotificationType.UnknownError);
      } finally {
        dispatch(setLoadingState({ type: LoadingType.AccountSettingsProcessing, loading: false }));
      }

      return;
    }

    if (!isNeedToCheckPassword && !isNeedToVerificateCode) {
      if (popupType === AccountSettingsPopupType.EmailAddress) {
        await handleOnResendEmailCode();
      }

      if (popupType === AccountSettingsPopupType.PhoneNumber) {
        await handleOnResendPhoneCode();
      }

      dispatch(setLoadingState({ type: LoadingType.AccountSettingsProcessing, loading: false }));
    }
  };

  const getPasswordInput = (inputPasswordType: string) => {
    return (
      <PasswordInput
        name={inputPasswordType}
        label={inputPasswordType}
        withPasswordRequirements={inputPasswordType === AccountSettingsInputName.NewPassword}
        errorMessage={errors[inputPasswordType]?.message}
        onChange={(event: any) => {
          setValue(inputPasswordType, event.target.value);

          if (errors[inputPasswordType]?.message) {
            trigger(inputPasswordType);
          }
        }}
        value={watcher[inputPasswordType]}
        inputContainerClassName={styles.inputContainer}
        register={register}
        validate={
          inputPasswordType === AccountSettingsInputName.ConfirmNewPassword
            ? (password) => password === watcher[AccountSettingsInputName.NewPassword] || 'New Passwords do not match'
            : undefined
        }
        isErrorWithoutMessage={
          (inputPasswordType === AccountSettingsInputName.NewPassword && null) || !!errors[inputPasswordType]?.message
        }
      />
    );
  };

  const handleVerifyCode = async (code: string) => {
    dispatch(setLoadingState({ type: LoadingType.CodeVerification, loading: true }));

    const result = await (popupType === AccountSettingsPopupType.PhoneNumber
      ? authApi.updatePhoneNumberByVerifyingCode(code)
      : authApi.updateEmailAddressByVerifyingCode(code));

    setIsCodeValid(result.success);

    if (result.success) {
      dispatch(
        setAccount(
          popupType === AccountSettingsPopupType.PhoneNumber
            ? { phone: watcher[AccountSettingsInputName.NewPhone] }
            : { email: watcher[AccountSettingsInputName.NewEmail] },
        ),
      );

      await dispatch(
        updateBorrower(
          popupType === AccountSettingsPopupType.PhoneNumber
            ? { phone: watcher[AccountSettingsInputName.NewPhone] }
            : { email: watcher[AccountSettingsInputName.NewEmail] },
        ),
      ).unwrap();

      if (applicationId) {
        await applicationApi.updateApplication({
          applicationId: applicationId as string,
          applicationVariables:
            popupType === AccountSettingsPopupType.PhoneNumber
              ? { borrower_phone: watcher[AccountSettingsInputName.NewPhone] }
              : { borrower_email: watcher[AccountSettingsInputName.NewEmail]?.toLowerCase() },
        });
      }

      notificationManager.notifySuccess(
        popupType === AccountSettingsPopupType.PhoneNumber
          ? SuccessNotification.PhoneNumberChangedSuccessfully
          : SuccessNotification.EmailChangedSuccessfully,
      );

      handleOnClose();
    }

    dispatch(setLoadingState({ type: LoadingType.CodeVerification, loading: false }));
  };

  const getInputForNewData = () => {
    if (!isNeedToCheckPassword && !isNeedToVerificateCode) {
      switch (popupType) {
        case AccountSettingsPopupType.PhoneNumber:
          return (
            <Controller
              name={AccountSettingsInputName.NewPhone}
              control={control}
              rules={{
                required: getMessageForRequiredFields(AccountSettingsInputName.NewPhone),
                validate: validatePhoneNumber,
              }}
              render={() => (
                <PhoneNumberInput
                  label={`Please enter new ${popupType}`}
                  errorMessage={errors[AccountSettingsInputName.NewPhone]?.message}
                  name={AccountSettingsInputName.NewPhone}
                  onChange={(value: string) => {
                    if (value && value !== watcher[AccountSettingsInputName.NewPhone]) {
                      setValue(AccountSettingsInputName.NewPhone, value);
                      trigger(AccountSettingsInputName.NewPhone);
                    }
                  }}
                  value={watcher[AccountSettingsInputName.NewPhone]}
                />
              )}
            />
          );
        case AccountSettingsPopupType.EmailAddress:
          return (
            <Input
              label={`Please enter new ${popupType}`}
              errorMessage={errors[AccountSettingsInputName.NewEmail]?.message}
              name={AccountSettingsInputName.NewEmail}
              onChange={(event: any) => {
                setValue(AccountSettingsInputName.NewEmail, removeSpaces(event.target.value?.toLowerCase()));
                trigger(AccountSettingsInputName.NewEmail);
              }}
              inputRef={
                register(AccountSettingsInputName.NewEmail, {
                  required: {
                    value: true,
                    message: getMessageForRequiredFields(AccountSettingsInputName.NewEmail),
                  },
                  validate: (value) => isValidEmail(value) || 'Please enter a valid email address',
                }).ref
              }
              value={watcher[AccountSettingsInputName.NewEmail]}
            />
          );
      }
    }
  };

  const getVerificationForm = () => {
    const getFormHeader = (sendCodeTo: string) => (
      <p className={styles.subtitle}>
        We've sent a 6-digit verification code to{' '}
        <a className={clsx({ [styles.email]: popupType === AccountSettingsPopupType.EmailAddress })}>{sendCodeTo}</a>
      </p>
    );

    if (!isNeedToCheckPassword && isNeedToVerificateCode) {
      switch (popupType) {
        case AccountSettingsPopupType.EmailAddress:
          return (
            <CodeVerificationForm
              handleVerificationCodeComplete={handleVerifyCode}
              isCodeVerifying={isCodeVerifying}
              isCodeValid={isCodeValid}
              alternativeFormHeader={getFormHeader(watcher[AccountSettingsInputName.NewEmail])}
              onResendCode={handleOnResendEmailCode}
            />
          );
        case AccountSettingsPopupType.PhoneNumber:
          return (
            <CodeVerificationForm
              handleVerificationCodeComplete={handleVerifyCode}
              isCodeVerifying={isCodeVerifying}
              isCodeValid={isCodeValid}
              alternativeFormHeader={getFormHeader(formatPhoneNumber(watcher[AccountSettingsInputName.NewPhone]))}
              onResendCode={handleOnResendPhoneCode}
            />
          );
      }
    }
  };

  const getPopupContent = () => {
    return (
      <FormBody formBodyClassName={styles.formBody} onSubmit={handleSubmit(onSubmit)}>
        {isAccountSettingsProcessing ? (
          <Loader containerWithoutMargin />
        ) : (
          <>
            <div className={styles.popupTitle}>{`Change ${popupType}`}</div>
            {isPasswordValidationTokenExpired && (
              <div className={styles.warningSubtitle}>
                <WarningImage />
                <div>Sorry, the time is out. Please try again</div>
              </div>
            )}
            {isNeedToCheckPassword && (
              <>
                {popupType !== AccountSettingsPopupType.Password && (
                  <div className={styles.popupText}>Please enter your password to verify identity</div>
                )}
                {getPasswordInput(
                  popupType === AccountSettingsPopupType.Password
                    ? AccountSettingsInputName.OldPassword
                    : AccountSettingsInputName.Password,
                )}
                {popupType == AccountSettingsPopupType.Password && (
                  <>
                    {getPasswordInput(AccountSettingsInputName.NewPassword)}
                    {getPasswordInput(AccountSettingsInputName.ConfirmNewPassword)}
                  </>
                )}
              </>
            )}
            {getInputForNewData()}
            {getVerificationForm()}
            <div className={styles.dialogButtonsContainer}>
              {!isNeedToVerificateCode && (
                <Button type="submit" form="form" buttonType={ButtonType.Primary}>
                  Submit
                </Button>
              )}
              <Button buttonType={ButtonType.Secondary} onClick={handleOnClose}>
                Cancel
              </Button>
            </div>
          </>
        )}
      </FormBody>
    );
  };

  return loading ? (
    <Loader />
  ) : (
    <div className={styles.accountSettingsContainer}>
      <div className={styles.settingsRow}>
        <div className={styles.settingsTitle}>Phone Number</div>
        <div>
          <div className={styles.settingsValue}>{formatPhoneNumber(phone)}</div>
          <div onClick={() => handleOpenPopup(AccountSettingsPopupType.PhoneNumber)} className={styles.changeButton}>
            Change Phone Number
          </div>
        </div>
      </div>
      <div className={styles.settingsRow}>
        <div className={styles.settingsTitle}>Email Address</div>
        <div className={styles.valueAndButtonContainer}>
          <div className={styles.settingsValue}>{email}</div>
          <div onClick={() => handleOpenPopup(AccountSettingsPopupType.EmailAddress)} className={styles.changeButton}>
            Change Email Address
          </div>
        </div>
      </div>
      <div className={styles.settingsRow}>
        <div className={styles.settingsTitle}>Password</div>
        <div>
          <div className={styles.settingsValue}>••••••••</div>
          <div onClick={() => handleOpenPopup(AccountSettingsPopupType.Password)} className={styles.changeButton}>
            Change Password
          </div>
        </div>
      </div>
      <BlurPopup blurDeep={2} onClose={handleOnClose} open={isPopupOpen}>
        {getPopupContent()}
      </BlurPopup>
    </div>
  );
};

export default AccountSettings;
